import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faDownload,
  faPrint,
  faEye,
  faList,
  faRefresh,
  faCalendarAlt,
  faUser,
  faUsers,
  faMoneyBillWave,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faChartPie,
  faFileExport,
  faCalculator,
  faSort,
  faSortUp,
  faSortDown,
  faChevronDown,
  faChevronUp,
  faTimes,
  faEdit,
  faReceipt,
  faHistory,
  faBell,
  faEnvelope,
  faPhone,
  faHome,
  faIdCard,
  faUniversity,
  faBook,
  faGraduationCap,
  faBuilding,
  faSchool,
  faVenusMars,
  faAddressCard,
  faPercentage,
  faCalendarDay,
  faSortAmountDown,
  faSortAmountUp,
  faExpandAlt,
  faCompressAlt,
  faFileAlt,
  faQuestionCircle,
  faTable,
  faLayerGroup,
  faChevronRight,
  faEllipsisV,
  faInfoCircle,
  faFilePdf,
  faListOl,
  faFileInvoice
} from "@fortawesome/free-solid-svg-icons";
import "./dashboard.css";
import "./FeeDefaulters.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const months = [
  { id: 1, name: "January", value: "January" },
  { id: 2, name: "February", value: "February" },
  { id: 3, name: "March", value: "March" },
  { id: 4, name: "April", value: "April" },
  { id: 5, name: "May", value: "May" },
  { id: 6, name: "June", value: "June" },
  { id: 7, name: "July", value: "July" },
  { id: 8, name: "August", value: "August" },
  { id: 9, name: "September", value: "September" },
  { id: 10, name: "October", value: "October" },
  { id: 11, name: "November", value: "November" },
  { id: 12, name: "December", value: "December" }
];

const familyTypes = [
  { id: "all", name: "All Families", icon: faUsers, color: "#7c3aed" },
  { id: "solo", name: "Solo Students", icon: faUser, color: "#3b82f6" },
  { id: "family", name: "Family Groups", icon: faUsers, color: "#8b5cf6" }
];

const paymentStatuses = [
  { id: "all", name: "All Statuses", color: "#6b7280", icon: faFilter },
  { id: "paid", name: "Paid", color: "#10b981", icon: faCheckCircle },
  { id: "unpaid", name: "Unpaid", color: "#ef4444", icon: faExclamationTriangle },
  { id: "partial", name: "Partial", color: "#f59e0b", icon: faClock }
];

const genders = [
  { id: "all", name: "All Genders", icon: faVenusMars },
  { id: "Male", name: "Male", icon: faUser },
  { id: "Female", name: "Female", icon: faUser }
];

const FeeDefaulters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for filters
  const [filters, setFilters] = useState({
    selectedMonths: [months.find(m => m.value === new Date().toLocaleString('default', { month: 'long' }))?.value] || [],
    familyType: "all",
    paymentStatus: "all",
    gender: "all",
    classFilter: "",
    sectionFilter: "",
    minAmount: "",
    maxAmount: "",
    searchQuery: "",
    startDate: "",
    endDate: ""
  });

  // State for data
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    student_counts: { total_students: 0, solo_students: 0, family_students: 0, students_without_fee: 0 },
    fee_counts: { students_with_fee_records: 0, total_defaulters: 0, solo_fee_records: 0, family_fee_records: 0 },
    amounts: { total_amount: 0, total_paid: 0, total_pending: 0 },
    current_month: { month: "", year: "", defaulters: 0, pending_amount: 0, paid_amount: 0 }
  });

  // State for UI
  const [sortConfig, setSortConfig] = useState({ key: "student_name", direction: "asc" });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [expandedStudents, setExpandedStudents] = useState([]);
  
  // CHANGED: Default to false (hidden)
  const [showFilters, setShowFilters] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [exportType, setExportType] = useState("allStudents");
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);
  
  // State for modals
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showFamilyTypeDropdown, setShowFamilyTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // State for dynamic template
  const [defaulterTemplate, setDefaulterTemplate] = useState("");

  // State for current logged-in user
  const [currentUser, setCurrentUser] = useState(null);

  // Ref for dropdowns
  const monthDropdownRef = useRef(null);
  const familyTypeDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) setShowMonthDropdown(false);
      if (familyTypeDropdownRef.current && !familyTypeDropdownRef.current.contains(event.target)) setShowFamilyTypeDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch template on mount
  useEffect(() => {
    fetchTemplate();
  }, []);

  // Load current user from localStorage
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setCurrentUser(JSON.parse(storedUser));
        } else {
          const token = localStorage.getItem('authToken');
          if (token) {
            fetchUserDetails(token).then(fetchedUser => {
              if (fetchedUser) {
                setCurrentUser(fetchedUser);
                localStorage.setItem('user', JSON.stringify(fetchedUser));
              }
            });
          }
        }
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
      }
    };
    loadUser();
  }, []);

  const fetchUserDetails = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user_details.php?action=get_user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch user details');
      const data = await response.json();
      if (data.success && data.user) {
        return data.user;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return null;
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/event_msg.php?action=get_template&event_type=fee_defaulter`);
      if (!response.ok) throw new Error('Failed to fetch template');
      const data = await response.json();
      if (data.success && data.template) {
        setDefaulterTemplate(data.template);
      } else {
        // Updated default template with clear structure and student details
        setDefaulterTemplate(`Dear Parent/Guardian,

This is a reminder from Modern High School regarding your child's fee.

Student: {{student_name}}
Father: {{father_name}}
Class: {{class}} - Section: {{section}}
Student ID: {{student_id}}

📌 Current Month Fee ({{target_month}}): PKR {{current_month_fee}}

📋 Pending for previous months:
{{pending_months_with_amounts}}

💰 Other Charges (Admission, Books, etc.): PKR {{expenses}}

🔴 Total Amount Due: PKR {{total_due}}

Please clear the dues at your earliest convenience.
You can pay online or visit the accounts office.

Thank you,
MHS Administration`);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setDefaulterTemplate(`Dear Parent/Guardian,

This is a reminder from Modern High School regarding your child's fee.

Student: {{student_name}}
Father: {{father_name}}
Class: {{class}} - Section: {{section}}
Student ID: {{student_id}}

📌 Current Month Fee ({{target_month}}): PKR {{current_month_fee}}

📋 Pending for previous months:
{{pending_months_with_amounts}}

💰 Other Charges (Admission, Books, etc.): PKR {{expenses}}

🔴 Total Amount Due: PKR {{total_due}}

Please clear the dues at your earliest convenience.
You can pay online or visit the accounts office.

Thank you,
MHS Administration`);
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchData();
  }, [
    filters.selectedMonths, 
    filters.familyType, 
    filters.paymentStatus, 
    filters.gender
  ]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, students, sortConfig]);

  const fetchData = async () => {
    if (filters.selectedMonths.length === 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/FeeDefaultersController.php?action=getAllStudentsWithFeeStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selectedMonths: filters.selectedMonths,
          familyType: filters.familyType,
          paymentStatus: filters.paymentStatus,
          gender: filters.gender,
          classFilter: filters.classFilter,
          sectionFilter: filters.sectionFilter,
          minAmount: filters.minAmount,
          maxAmount: filters.maxAmount,
          searchQuery: filters.searchQuery,
          startDate: filters.startDate,
          endDate: filters.endDate
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setStudents(result.data);
        setFilteredStudents(result.data);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error("Error fetching fee defaulters data:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load fee defaulters data.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/FeeDefaultersController.php?action=getStats`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStats(result.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSendInAppNotification = async () => {
    if (!selectedStudents || selectedStudents.length === 0) {
      Swal.fire({
        title: "No Students Selected",
        text: "Please select at least one student to send a notification.",
        icon: "warning",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    const selectedStudentObjects = students.filter(s => 
      selectedStudents.includes(s.student_db_id)
    );

    const validStudentsWithReceiver = selectedStudentObjects.map(student => ({
      ...student,
      receiver_id: student.student_db_id,
      receiver_mobile: student.mobile
    }));

    if (validStudentsWithReceiver.length === 0) {
      Swal.fire({
        title: "Missing Parent Accounts",
        text: "None of the selected students have a linked parent account or mobile number.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
      return;
    }

    if (!defaulterTemplate) {
      Swal.fire({
        title: "Template Missing",
        text: "Message template not loaded. Please refresh or contact admin.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Send In-App Notifications?",
      text: `Send ${validStudentsWithReceiver.length} fee reminders to the MHS Parent App?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, send them"
    });

    if (!confirmResult.isConfirmed) return;

    // --- FULLY FIXED TEMPLATE REPLACEMENT LOGIC ---
    const replacePlaceholders = (template, student) => {
      const financial = student.financial_summary || {};
      
      // 1. FORCE NUMBERS: Prevent JS string concatenation bugs
      const currentMonthFee = Number(financial.current_month_fee || student.overall_status?.total_monthly_fee || 0);
      const previousPending = Number(financial.previous_pending || 0);
      const totalExpensePending = Number(financial.total_expense_pending || 0);
      
      // 2. EXPLICIT MATH: Sum the total amount due
      const totalDue = currentMonthFee + previousPending + totalExpensePending;

      const targetMonth = filters.selectedMonths.length > 0 
        ? filters.selectedMonths[filters.selectedMonths.length - 1] 
        : new Date().toLocaleString('default', { month: 'long' });

      // Grab the pre-formatted bulleted list from PHP
      const pendingMonthsList = financial.pending_months_with_amounts || 'None';

      const numberFormatter = new Intl.NumberFormat('en-PK', { minimumFractionDigits: 0 });
      const formattedCurrentFee = numberFormatter.format(currentMonthFee);
      const formattedExpensePending = numberFormatter.format(totalExpensePending);
      const formattedTotalDue = numberFormatter.format(totalDue);

      let message = template;
      
      // Replace Core Tags
      message = message.replace(/{{student_name}}/g, student.student_name || '');
      message = message.replace(/{{father_name}}/g, student.father_name || '');
      message = message.replace(/{{class}}/g, student.class || '');
      message = message.replace(/{{section}}/g, student.section || '');
      message = message.replace(/{{student_id}}/g, student.student_id || '');
      message = message.replace(/{{target_month}}/g, targetMonth);

      // Replace Financial Tags
      message = message.replace(/{{current_month_fee}}/g, formattedCurrentFee);
      message = message.replace(/{{expenses}}/g, formattedExpensePending);
      message = message.replace(/{{pending_months_with_amounts}}/g, pendingMonthsList);
      
      // Replace Final Total Due Tag
      message = message.replace(/{{total_due}}/g, formattedTotalDue);

      return message;
    };

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    const senderId = currentUser?.id || 1;
    const senderMobile = currentUser?.mobile || '00000000000';

    for (const student of validStudentsWithReceiver) {
      const messageBody = replacePlaceholders(defaulterTemplate, student);

      const payload = {
        sender_id: senderId,
        sender_type: "admin",
        sender_mobile: senderMobile,
        receiver_id: student.receiver_id,
        receiver_type: "parent",
        receiver_mobile: student.receiver_mobile,
        related_student_id: student.student_db_id,
        message_type: "fee_reminder",
        message_body: messageBody
      };

      try {
        const response = await fetch(`${API_BASE_URL}/messages_api.php?action=send_message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload)
        });

        const responseData = await response.json();
        
        if (response.ok && responseData.success) {
          successCount++;
        } else {
          failCount++;
          console.error('API error for', student.student_name, ':', responseData.error || 'Unknown error');
        }
      } catch (error) {
        console.error("Error sending in-app notification for", student.student_name, error);
        failCount++;
      }
    }

    setLoading(false);

    Swal.fire({
      title: "Notifications Sent!",
      html: `Successfully sent <b>${successCount}</b> notifications to the MHS Parent App.<br/>Failed: ${failCount}`,
      icon: failCount === 0 ? "success" : "warning",
      confirmButtonColor: "#2E2A74",
    });

    setSelectedStudents([]);
  };

  const applyFilters = () => {
    let filtered = [...students];

    if (filters.familyType !== "all") {
      filtered = filtered.filter(student => 
        student.family_status && 
        student.family_status.toLowerCase() === filters.familyType.toLowerCase()
      );
    }

    if (filters.gender !== "all") {
      filtered = filtered.filter(student => 
        student.gender && 
        student.gender.toLowerCase() === filters.gender.toLowerCase()
      );
    }

    if (filters.paymentStatus !== "all") {
      filtered = filtered.filter(student => 
        student.overall_status?.status && 
        student.overall_status.status.toLowerCase() === filters.paymentStatus.toLowerCase()
      );
    }

    if (filters.classFilter) {
      filtered = filtered.filter(student => 
        student.class && student.class.toLowerCase().includes(filters.classFilter.toLowerCase())
      );
    }

    if (filters.sectionFilter) {
      filtered = filtered.filter(student => 
        student.section && student.section.toLowerCase().includes(filters.sectionFilter.toLowerCase())
      );
    }

    if (filters.minAmount) {
      filtered = filtered.filter(student => 
        student.overall_status?.total_pending >= parseFloat(filters.minAmount)
      );
    }

    if (filters.maxAmount) {
      filtered = filtered.filter(student => 
        student.overall_status?.total_pending <= parseFloat(filters.maxAmount)
      );
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(student =>
        (student.student_name && student.student_name.toLowerCase().includes(query)) ||
        (student.father_name && student.father_name.toLowerCase().includes(query)) ||
        (student.student_id && student.student_id.toLowerCase().includes(query)) ||
        (student.family_code && student.family_code.toLowerCase().includes(query)) ||
        (student.mobile && student.mobile.includes(query)) ||
        (student.b_form_no && student.b_form_no.toLowerCase().includes(query)) ||
        (student.address && student.address.toLowerCase().includes(query))
      );
    }

    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key.startsWith('monthly_status.')) {
        const month = sortConfig.key.split('.')[1];
        aValue = a.monthly_status?.[month]?.['total_pending'] || 0;
        bValue = b.monthly_status?.[month]?.['total_pending'] || 0;
      } else if (sortConfig.key === 'overall_status.total_pending') {
        aValue = a.overall_status?.total_pending || 0;
        bValue = b.overall_status?.total_pending || 0;
      } else {
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
        
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredStudents(filtered);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleMonthSelection = (month) => {
    setFilters(prev => {
      const isSelected = prev.selectedMonths.includes(month);
      if (isSelected) {
        return {
          ...prev,
          selectedMonths: prev.selectedMonths.filter(m => m !== month)
        };
      } else {
        return {
          ...prev,
          selectedMonths: [...prev.selectedMonths, month]
        };
      }
    });
  };

  const selectAllMonths = () => {
    setFilters(prev => ({
      ...prev,
      selectedMonths: months.map(m => m.value)
    }));
  };

  const clearAllMonths = () => {
    setFilters(prev => ({
      ...prev,
      selectedMonths: []
    }));
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const selectAllStudents = () => {
    const currentIds = currentStudents.map(student => student.student_db_id);
    const allSelected = currentIds.every(id => selectedStudents.includes(id));
    if (allSelected) {
      setSelectedStudents(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
      setSelectedStudents(prev => {
        const newSelection = new Set(prev);
        currentIds.forEach(id => newSelection.add(id));
        return Array.from(newSelection);
      });
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'paid':
        return { label: "Paid", color: "#10b981", icon: faCheckCircle };
      case 'unpaid':
        return { label: "Unpaid", color: "#ef4444", icon: faExclamationTriangle };
      case 'partial':
        return { label: "Partial", color: "#f59e0b", icon: faClock };
      default:
        return { label: "Unknown", color: "#6b7280", icon: faQuestionCircle };
    }
  };

  const openStudentDetailsModal = (student) => {
    setSelectedStudent(student);
    setShowStudentDetails(true);
  };

  const openPaymentHistoryModal = (student) => {
    setSelectedStudent(student);
    setShowPaymentHistory(true);
  };

  const generatePDF = () => {
    setLoading(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      
      doc.setFillColor(79, 70, 229);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text("MODERN HIGH SCHOOL", pageWidth / 2, 12, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text("FEE DEFAULTERS SUMMARY REPORT", pageWidth / 2, 18, { align: 'center' });

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(`Generated Date: ${new Date().toLocaleString()}`, 15, 35);
      doc.text(`Months: ${filters.selectedMonths.join(', ')}`, 15, 40);
      
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(pageWidth - 85, 30, 70, 20, 2, 2, 'FD');
      doc.text("Total Pending Amount", pageWidth - 80, 38);
      doc.setFontSize(14);
      doc.text(`PKR ${summary.totalPending.toLocaleString()}`, pageWidth - 80, 45);

      const tableColumn = [
        "ID", "Student Name", "Father Name", "Class", "Section", 
        "Current Fee", "Previous Pending", "Expenses", "Total Pending", "Status"
      ];
      const tableRows = filteredStudents.map(s => [
        s.student_id,
        s.student_name,
        s.father_name,
        s.class,
        s.section || 'N/A',
        s.financial_summary?.current_month_fee || 0,
        s.financial_summary?.previous_pending || 0,
        s.financial_summary?.total_expense_pending || 0,
        s.overall_status?.total_pending || 0,
        s.overall_status?.status.toUpperCase()
      ]);

      autoTable(doc, {
        startY: 55,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          8: { fontStyle: 'bold', textColor: [220, 38, 38] }
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.text(`Page ${data.pageNumber}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
        }
      });

      doc.save(`Fee_Defaulters_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'Your professional report has been downloaded.',
        confirmButtonColor: '#4f46e5'
      });

    } catch (error) {
      console.error("PDF Error:", error);
      Swal.fire({ icon: 'error', title: 'Generation Failed', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (format === 'pdf') {
      await generatePDF();
      return;
    }
    
    try {
      const selectedIds = selectedStudents.length > 0 ? selectedStudents : 
                         filteredStudents.map(s => s.student_db_id);
      
      const response = await fetch(`${API_BASE_URL}/FeeDefaultersController.php?action=export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          format,
          exportType: exportType,
          recordIds: selectedIds,
          filters: filters
        })
      });

      if (response.ok) {
        if (format === 'csv') {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = exportType === 'allStudents' 
            ? `all_students_fee_status_${new Date().toISOString().split('T')[0]}.csv`
            : `fee_defaulters_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          Swal.fire({
            title: "Export Successful!",
            text: "Data exported as CSV successfully",
            icon: "success",
            confirmButtonColor: "#10b981",
          });
        }
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire({
        title: "Export Failed",
        text: "Failed to export data",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const clearFilters = () => {
    const currentMonth = months.find(m => m.value === new Date().toLocaleString('default', { month: 'long' }))?.value;
    setFilters({
      selectedMonths: currentMonth ? [currentMonth] : [],
      familyType: "all",
      paymentStatus: "all",
      gender: "all",
      classFilter: "",
      sectionFilter: "",
      minAmount: "",
      maxAmount: "",
      searchQuery: "",
      startDate: "",
      endDate: ""
    });
    setSelectedStudents([]);
  };

  const refreshData = () => {
    fetchData();
    fetchStats();
    Swal.fire({
      title: "Refreshed!",
      text: "Data has been refreshed",
      icon: "success",
      timer: 1500,
      showConfirmButton: false
    });
  };

  const calculateSummary = () => {
    const totalPending = filteredStudents.reduce((sum, student) => 
      sum + (student.overall_status?.total_pending || 0), 0
    );
    const totalPaid = filteredStudents.reduce((sum, student) => 
      sum + (student.overall_status?.total_paid || 0), 0
    );
    const totalMonthlyFee = filteredStudents.reduce((sum, student) => 
      sum + (student.overall_status?.total_monthly_fee || 0), 0
    );
    const totalPreviousPending = filteredStudents.reduce((sum, student) => 
      sum + (student.financial_summary?.previous_pending || 0), 0
    );
    const totalExpenses = filteredStudents.reduce((sum, student) => 
      sum + (student.financial_summary?.total_expense_pending || 0), 0
    );

    return {
      totalPending,
      totalPaid,
      totalMonthlyFee,
      totalPreviousPending,
      totalExpenses,
      defaultersCount: filteredStudents.filter(s => 
        s.overall_status?.status === 'unpaid' || s.overall_status?.status === 'partial'
      ).length
    };
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredStudents.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfMax = Math.floor(maxPagesToShow / 2);
      let start = Math.max(currentPage - halfMax, 1);
      let end = Math.min(start + maxPagesToShow - 1, totalPages);
      
      if (end - start + 1 < maxPagesToShow) {
        start = Math.max(end - maxPagesToShow + 1, 1);
      }
      
      if (start > 1) pageNumbers.push(1);
      if (start > 2) pageNumbers.push('...');
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      
      if (end < totalPages - 1) pageNumbers.push('...');
      if (end < totalPages) pageNumbers.push(totalPages);
    }

    return (
      <div className="FeeDefaulters-pagination">
        <button 
          className="FeeDefaulters-pageBtn"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faChevronDown} rotation={270} />
        </button>
        
        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} className="FeeDefaulters-pageEllipsis">...</span>
          ) : (
            <button
              key={number}
              className={`FeeDefaulters-pageBtn ${currentPage === number ? 'FeeDefaulters-pageBtnActive' : ''}`}
              onClick={() => paginate(number)}
            >
              {number}
            </button>
          )
        ))}
        
        <button 
          className="FeeDefaulters-pageBtn"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <FontAwesomeIcon icon={faChevronDown} rotation={-90} />
        </button>
      </div>
    );
  };

  const renderPaymentHistoryModal = () => {
    if (!selectedStudent || !showPaymentHistory) return null;

    const allMonths = months.map(m => m.value);
    const paidMonths = [];
    const unpaidMonths = [];
    const partialMonths = [];

    allMonths.forEach(month => {
      const status = selectedStudent.monthly_status?.[month]?.status || 'unpaid';
      if (status === 'paid') paidMonths.push(month);
      else if (status === 'unpaid') unpaidMonths.push(month);
      else if (status === 'partial') partialMonths.push(month);
    });

    const totalPaid = selectedStudent.overall_status?.total_paid || 0;
    const totalPending = selectedStudent.overall_status?.total_pending || 0;
    const totalFee = selectedStudent.overall_status?.total_monthly_fee || 0;
    const paidPercentage = totalFee > 0 ? (totalPaid / totalFee) * 100 : 0;

    return (
      <div className="FeeDefaulters-modal-overlay">
        <div className="FeeDefaulters-modal FeeDefaulters-payment-history-modal">
          <div className="FeeDefaulters-modal-header">
            <h3>
              <FontAwesomeIcon icon={faHistory} />
              Payment History - {selectedStudent.student_name}
            </h3>
            <button 
              className="FeeDefaulters-modal-close"
              onClick={() => setShowPaymentHistory(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="FeeDefaulters-modal-content">
            <div className="FeeDefaulters-month-cards">
              {allMonths.map(month => {
                const status = selectedStudent.monthly_status?.[month]?.status || 'unpaid';
                const statusInfo = getStatusDisplay(status);
                
                return (
                  <div 
                    key={month} 
                    className={`FeeDefaulters-month-card FeeDefaulters-month-card-${status}`}
                    style={{ borderColor: statusInfo.color }}
                  >
                    <div className="FeeDefaulters-month-card-header">
                      {month.substring(0, 3)}
                    </div>
                    <div 
                      className="FeeDefaulters-month-card-status"
                      style={{ backgroundColor: statusInfo.color }}
                    >
                      <FontAwesomeIcon icon={statusInfo.icon} />
                    </div>
                    <div className="FeeDefaulters-month-card-footer">
                      {statusInfo.label}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="FeeDefaulters-payment-summary">
              <div className="FeeDefaulters-summary-row">
                <span className="FeeDefaulters-summary-label">Paid Months:</span>
                <span className="FeeDefaulters-summary-value FeeDefaulters-paid">
                  {paidMonths.join(', ')}
                </span>
              </div>
              <div className="FeeDefaulters-summary-row">
                <span className="FeeDefaulters-summary-label">Unpaid Months:</span>
                <span className="FeeDefaulters-summary-value FeeDefaulters-unpaid">
                  {unpaidMonths.join(', ')}
                </span>
              </div>
              <div className="FeeDefaulters-summary-row">
                <span className="FeeDefaulters-summary-label">Partial Months:</span>
                <span className="FeeDefaulters-summary-value FeeDefaulters-partial">
                  {partialMonths.join(', ')}
                </span>
              </div>
            </div>
            
            <div className="FeeDefaulters-payment-stats">
              <div className="FeeDefaulters-stat-card">
                <div className="FeeDefaulters-stat-icon" style={{ backgroundColor: '#10b981' }}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="FeeDefaulters-stat-content">
                  <div className="FeeDefaulters-stat-value">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(totalPaid)}
                  </div>
                  <div className="FeeDefaulters-stat-label">Total Paid</div>
                </div>
              </div>
              
              <div className="FeeDefaulters-stat-card">
                <div className="FeeDefaulters-stat-icon" style={{ backgroundColor: '#ef4444' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className="FeeDefaulters-stat-content">
                  <div className="FeeDefaulters-stat-value">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(totalPending)}
                  </div>
                  <div className="FeeDefaulters-stat-label">Total Pending</div>
                </div>
              </div>
              
              <div className="FeeDefaulters-stat-card">
                <div className="FeeDefaulters-stat-icon" style={{ backgroundColor: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faPercentage} />
                </div>
                <div className="FeeDefaulters-stat-content">
                  <div className="FeeDefaulters-stat-value">
                    {paidPercentage.toFixed(1)}%
                  </div>
                  <div className="FeeDefaulters-stat-label">Payment Percentage</div>
                </div>
              </div>
            </div>
            
            <div className="FeeDefaulters-payment-chart">
              <div className="FeeDefaulters-chart-header">
                <h4>Payment Progress</h4>
              </div>
              <div className="FeeDefaulters-chart-bar">
                <div 
                  className="FeeDefaulters-chart-progress"
                  style={{ width: `${Math.min(paidPercentage, 100)}%` }}
                >
                  <span>{paidPercentage.toFixed(1)}%</span>
                </div>
              </div>
              <div className="FeeDefaulters-chart-labels">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
          
          <div className="FeeDefaulters-modal-footer">
            <button 
              className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
              onClick={() => setShowPaymentHistory(false)}
            >
              Close
            </button>
            <button 
              className="FeeDefaulters-btn FeeDefaulters-btnPrimary"
              onClick={() => navigate(`/fee-payment/${selectedStudent.student_id}`)}
            >
              <FontAwesomeIcon icon={faMoneyBillWave} /> Record Payment
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStudentDetailsModal = () => {
    if (!selectedStudent || !showStudentDetails) return null;

    return (
      <div className="FeeDefaulters-modal-overlay">
        <div className="FeeDefaulters-modal FeeDefaulters-student-details-modal">
          <div className="FeeDefaulters-modal-header">
            <h3>
              <FontAwesomeIcon icon={faUser} />
              Student Details - {selectedStudent.student_name}
            </h3>
            <button 
              className="FeeDefaulters-modal-close"
              onClick={() => setShowStudentDetails(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="FeeDefaulters-modal-content">
            <div className="FeeDefaulters-student-details-grid">
              <div className="FeeDefaulters-detail-section">
                <h4>Personal Information</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Student ID:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.student_id}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Full Name:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.student_name}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Father's Name:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.father_name}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Gender:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.gender}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">B-Form No:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.b_form_no || 'N/A'}</span>
                </div>
              </div>
              
              <div className="FeeDefaulters-detail-section">
                <h4>Contact Information</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Mobile:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.mobile || 'N/A'}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Address:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.address || 'N/A'}</span>
                </div>
              </div>
              
              <div className="FeeDefaulters-detail-section">
                <h4>Academic Information</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Class:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.class || 'N/A'}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Section:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.section || 'N/A'}</span>
                </div>
              </div>
              
              <div className="FeeDefaulters-detail-section">
                <h4>Family Information</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Family Status:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.family_status || 'Solo'}</span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Family Code:</span>
                  <span className="FeeDefaulters-detail-value">{selectedStudent.family_code || 'N/A'}</span>
                </div>
              </div>
              
              <div className="FeeDefaulters-detail-section">
                <h4>Fee Status</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Overall Status:</span>
                  <span 
                    className="FeeDefaulters-detail-value FeeDefaulters-status-badge"
                    style={{ 
                      backgroundColor: getStatusDisplay(selectedStudent.overall_status?.status).color 
                    }}
                  >
                    {getStatusDisplay(selectedStudent.overall_status?.status).label}
                  </span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Current Month Fee:</span>
                  <span className="FeeDefaulters-detail-value">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(selectedStudent.financial_summary?.current_month_fee || 0)}
                  </span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Previous Pending:</span>
                  <span className="FeeDefaulters-detail-value FeeDefaulters-unpaid">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(selectedStudent.financial_summary?.previous_pending || 0)}
                  </span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Other Expenses:</span>
                  <span className="FeeDefaulters-detail-value FeeDefaulters-unpaid">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(selectedStudent.financial_summary?.total_expense_pending || 0)}
                  </span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Total Pending:</span>
                  <span className="FeeDefaulters-detail-value FeeDefaulters-unpaid">
                    {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(selectedStudent.overall_status?.total_pending || 0)}
                  </span>
                </div>
              </div>
              
              <div className="FeeDefaulters-detail-section">
                <h4>System Information</h4>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Created At:</span>
                  <span className="FeeDefaulters-detail-value">
                    {selectedStudent.student_created_at ? new Date(selectedStudent.student_created_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="FeeDefaulters-detail-row">
                  <span className="FeeDefaulters-detail-label">Last Updated:</span>
                  <span className="FeeDefaulters-detail-value">
                    {selectedStudent.student_updated_at ? new Date(selectedStudent.student_updated_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="FeeDefaulters-modal-footer">
            <button 
              className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
              onClick={() => setShowStudentDetails(false)}
            >
              Close
            </button>
            <button 
              className="FeeDefaulters-btn FeeDefaulters-btnPrimary"
              onClick={() => {
                setShowStudentDetails(false);
                openPaymentHistoryModal(selectedStudent);
              }}
            >
              <FontAwesomeIcon icon={faHistory} /> View Payment History
            </button>
          </div>
        </div>
      </div>
    );
  };

  const summary = calculateSummary();

  if (loading && students.length === 0) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="main-content" style={{ marginLeft: "20%" }}>
          <div className="FeeDefaulters-loading">
            <div className="FeeDefaulters-spinner"></div>
            <p>Loading fee defaulters data...</p>
            <p className="FeeDefaulters-loadingSubtext">
              Analyzing {filters.selectedMonths.length} selected month(s)
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="main-content" style={{ marginLeft: "20%" }}>
        <section className="content">
          <div className="FeeDefaulters-header" >
            <div className="FeeDefaulters-headerContent">
              <h1 className="FeeDefaulters-title">
                <FontAwesomeIcon icon={faExclamationTriangle} className="FeeDefaulters-headerIcon" />
                Fee Defaulters Management
              </h1>
              <p className="FeeDefaulters-subtitle">
                Track student fee payments across multiple months with detailed status analysis
              </p>
            </div>
            
            <div className="FeeDefaulters-headerActions" >
              
              {/* REPLACED: Standard button with dark toggle switch */}
              <div className="FeeDefaulters-toggle-container" onClick={() => setShowFilters(!showFilters)}>
                <span className="FeeDefaulters-toggle-text">
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </span>
                <div className={`FeeDefaulters-toggle-switch ${showFilters ? "active" : ""}`}>
                  <div className="FeeDefaulters-toggle-slider"></div>
                </div>
              </div>
              
              {/* <div className="FeeDefaulters-dropdown">
                <button className="FeeDefaulters-btn FeeDefaulters-btnExport">
                  <FontAwesomeIcon icon={faDownload} /> Export
                </button>
                <div className="FeeDefaulters-dropdownContent">
                  <div className="FeeDefaulters-exportOptions">
                    <label>Export Type:</label>
                    <select 
                      value={exportType}
                      onChange={(e) => setExportType(e.target.value)}
                      className="FeeDefaulters-exportSelect"
                    >
                      <option value="allStudents">All Students with Fee Status</option>
                      <option value="defaulters">Defaulters Only</option>
                    </select>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleExport('csv'); }}>
                    <FontAwesomeIcon icon={faFileExport} /> Export as CSV
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleExport('pdf'); }}>
                    <FontAwesomeIcon icon={faFilePdf} /> Export as PDF (Professional)
                  </a>
                </div>
              </div> */}
              
              <button 
                className="FeeDefaulters-btn FeeDefaulters-btnPrimary"
                onClick={handleSendInAppNotification}
                disabled={selectedStudents.length === 0}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                Send In-App Notification ({selectedStudents.length})
              </button>
              
              <button 
                className="FeeDefaulters-btn FeeDefaulters-btnRefresh"
                onClick={refreshData}
                title="Refresh Data"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>
            </div>
          </div>

          <div className="FeeDefaulters-statsOverview">
            <div className="FeeDefaulters-statCard">
              <div className="FeeDefaulters-statIcon FeeDefaulters-statTotal">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="FeeDefaulters-statDetails">
                <div className="FeeDefaulters-statValue">{stats.student_counts.total_students}</div>
                <div className="FeeDefaulters-statLabel">Total Students</div>
                <div className="FeeDefaulters-statSubtext">
                  {stats.student_counts.solo_students} Solo • {stats.student_counts.family_students} Family
                </div>
              </div>
            </div>
            
            <div className="FeeDefaulters-statCard">
              <div className="FeeDefaulters-statIcon FeeDefaulters-statDefaulters">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="FeeDefaulters-statDetails">
                <div className="FeeDefaulters-statValue">{summary.defaultersCount}</div>
                <div className="FeeDefaulters-statLabel">Current Defaulters</div>
                <div className="FeeDefaulters-statSubtext">
                  {filters.selectedMonths.length} month(s) selected
                </div>
              </div>
            </div>
            
            <div className="FeeDefaulters-statCard" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: 'auto' }}>
              <div className="FeeDefaulters-statIcon FeeDefaulters-statPending" style={{ fontSize: '22px', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <div className="FeeDefaulters-statDetails" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <div className="FeeDefaulters-statValue" style={{ fontSize: '20px', fontWeight: 600, lineHeight: '1.2', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 }).format(summary.totalPending)}
                </div>
                <div className="FeeDefaulters-statLabel" style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Total Pending</div>
                <div className="FeeDefaulters-statSubtext" style={{ fontSize: '12px', color: '#999', marginTop: '2px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {summary.totalMonthlyFee > 0 ? 
                    `${((summary.totalPending / summary.totalMonthlyFee) * 100).toFixed(1)}% of total fee` 
                    : 'No fee data'}
                </div>
              </div>
            </div>
            
            <div className="FeeDefaulters-statCard">
              <div className="FeeDefaulters-statIcon FeeDefaulters-statCurrent">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <div className="FeeDefaulters-statDetails">
                <div className="FeeDefaulters-statValue">{stats.current_month.defaulters || 0}</div>
                <div className="FeeDefaulters-statLabel">{stats.current_month.month} Defaulters</div>
                <div className="FeeDefaulters-statSubtext">
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(stats.current_month.pending_amount || 0)} pending
                </div>
              </div>
            </div>
          </div>

          {/* CONDITIONALLY RENDERED FILTERS SECTION (HIDDEN BY DEFAULT) */}
          {showFilters && (
            <div className="FeeDefaulters-filterSection">
              <div className="FeeDefaulters-filterHeader">
                <h3>
                  <FontAwesomeIcon icon={faFilter} /> Advanced Filters
                </h3>
                <div className="FeeDefaulters-filterActions">
                  <button 
                    className="FeeDefaulters-clearFilters"
                    onClick={clearFilters}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Clear All
                  </button>
                  <button 
                    className="FeeDefaulters-btn FeeDefaulters-btnApply"
                    onClick={fetchData}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
              
              <div className="FeeDefaulters-filterGrid">
                <div className="FeeDefaulters-filterGroup" ref={monthDropdownRef} style={{zIndex:1111111111111111111}}>
                  <label className="FeeDefaulters-filterLabel">
                    <FontAwesomeIcon icon={faCalendarAlt} /> Select Months
                    <span className="FeeDefaulters-filterBadge">{filters.selectedMonths.length}</span>
                  </label>
                  <div className="FeeDefaulters-dropdown-container">
                    <button 
                      className="FeeDefaulters-dropdown-toggle"
                      onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                    >
                      <span>
                        {filters.selectedMonths.length === 0 
                          ? 'Select Months' 
                          : filters.selectedMonths.length === 1 
                            ? filters.selectedMonths[0]
                            : `${filters.selectedMonths.length} months selected`
                        }
                      </span>
                      <FontAwesomeIcon icon={showMonthDropdown ? faChevronUp : faChevronDown} />
                    </button>
                    {showMonthDropdown && (
                      <div className="FeeDefaulters-dropdown-menu FeeDefaulters-month-dropdown">
                        <div className="FeeDefaulters-dropdown-header">
                          <button onClick={selectAllMonths}>Select All</button>
                          <button onClick={clearAllMonths}>Clear All</button>
                        </div>
                        <div className="FeeDefaulters-checkbox-list">
                          {months.map(month => (
                            <label key={month.id} className="FeeDefaulters-checkbox-item">
                              <input
                                type="checkbox"
                                checked={filters.selectedMonths.includes(month.value)}
                                onChange={() => handleMonthSelection(month.value)}
                              />
                              <span className="FeeDefaulters-checkbox-custom"></span>
                              <span className="FeeDefaulters-checkbox-label">{month.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="FeeDefaulters-filterRow">
                  <div className="FeeDefaulters-filterGroup FeeDefaulters-filterGroup-small" style={{zIndex:1111}} ref={familyTypeDropdownRef}>
                    <label className="FeeDefaulters-filterLabel">
                      <FontAwesomeIcon icon={faUsers} /> Student Type
                    </label>
                    <div className="FeeDefaulters-dropdown-container">
                      <button 
                        className="FeeDefaulters-dropdown-toggle"
                        onClick={() => setShowFamilyTypeDropdown(!showFamilyTypeDropdown)}
                      >
                        <span>
                          {familyTypes.find(t => t.id === filters.familyType)?.name || 'Select Type'}
                        </span>
                        <FontAwesomeIcon icon={showFamilyTypeDropdown ? faChevronUp : faChevronDown} />
                      </button>
                      {showFamilyTypeDropdown && (
                        <div className="FeeDefaulters-dropdown-menu">
                          {familyTypes.map(type => (
                            <button
                              key={type.id}
                              className={`FeeDefaulters-dropdown-item ${filters.familyType === type.id ? 'FeeDefaulters-dropdown-item-active' : ''}`}
                              onClick={() => {
                                setFilters(prev => ({ ...prev, familyType: type.id }));
                                setShowFamilyTypeDropdown(false);
                              }}
                            >
                              <FontAwesomeIcon icon={type.icon} style={{ color: type.color, marginRight: '8px' }} />
                              {type.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="FeeDefaulters-filterGroup FeeDefaulters-filterGroup-small" style={{zIndex:1111}} ref={statusDropdownRef}>
                    <label className="FeeDefaulters-filterLabel">
                      <FontAwesomeIcon icon={faCheckCircle} /> Payment Status
                    </label>
                    <div className="FeeDefaulters-dropdown-container">
                      <button 
                        className="FeeDefaulters-dropdown-toggle"
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      >
                        <span>
                          {paymentStatuses.find(s => s.id === filters.paymentStatus)?.name || 'Select Status'}
                        </span>
                        <FontAwesomeIcon icon={showStatusDropdown ? faChevronUp : faChevronDown} />
                      </button>
                      {showStatusDropdown && (
                        <div className="FeeDefaulters-dropdown-menu">
                          {paymentStatuses.map(status => (
                            <button
                              key={status.id}
                              className={`FeeDefaulters-dropdown-item ${filters.paymentStatus === status.id ? 'FeeDefaulters-dropdown-item-active' : ''}`}
                              onClick={() => {
                                setFilters(prev => ({ ...prev, paymentStatus: status.id }));
                                setShowStatusDropdown(false);
                              }}
                            >
                              <FontAwesomeIcon icon={status.icon} style={{ color: status.color, marginRight: '8px' }} />
                              {status.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="FeeDefaulters-advance-filters-toggle" >
                  <button 
                    className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
                    onClick={() => setShowAdvanceFilters(!showAdvanceFilters)}
                    style={{ background: "#7209b7"}}
                  >
                    <FontAwesomeIcon icon={showAdvanceFilters ? faChevronUp : faChevronDown} />
                    {showAdvanceFilters ? 'Hide Advance Filters' : 'Show Advance Filters'}
                  </button>
                </div>

                {showAdvanceFilters && (
                  <div className="FeeDefaulters-advance-filters">
                    <div className="FeeDefaulters-filterGroup" >
                      <label className="FeeDefaulters-filterLabel">
                        <FontAwesomeIcon icon={faGraduationCap} /> Academic Details
                      </label>
                      <div className="FeeDefaulters-classSectionGrid">
                        <div className="FeeDefaulters-classSectionGroup">
                          <label>Class:</label>
                          <div className="FeeDefaulters-classInputWrapper">
                            <FontAwesomeIcon icon={faSchool} className="FeeDefaulters-classIcon" />
                            <input
                              type="text"
                              placeholder="e.g., Class 1, 2, 3..."
                              value={filters.classFilter}
                              onChange={(e) => setFilters(prev => ({ ...prev, classFilter: e.target.value }))}
                              className="FeeDefaulters-classInput"
                            />
                            {filters.classFilter && (
                              <button 
                                className="FeeDefaulters-clearInput"
                                onClick={() => setFilters(prev => ({ ...prev, classFilter: "" }))}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="FeeDefaulters-classSectionGroup">
                          <label>Section:</label>
                          <div className="FeeDefaulters-classInputWrapper">
                            <FontAwesomeIcon icon={faLayerGroup} className="FeeDefaulters-classIcon" />
                            <input
                              type="text"
                              placeholder="e.g., A, B, C..."
                              value={filters.sectionFilter}
                              onChange={(e) => setFilters(prev => ({ ...prev, sectionFilter: e.target.value }))}
                              className="FeeDefaulters-classInput"
                            />
                            {filters.sectionFilter && (
                              <button 
                                className="FeeDefaulters-clearInput"
                                onClick={() => setFilters(prev => ({ ...prev, sectionFilter: "" }))}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="FeeDefaulters-filterGroup">
                      <label className="FeeDefaulters-filterLabel">
                        <FontAwesomeIcon icon={faCalculator} /> Pending Amount Range
                      </label>
                      <div className="FeeDefaulters-amountFilter">
                        <div className="FeeDefaulters-amountInputGroup">
                          <label>
                            <FontAwesomeIcon icon={faSortAmountDown} /> Minimum:
                          </label>
                          <div className="FeeDefaulters-amountInput">
                            <span className="FeeDefaulters-currencySymbol">PKR</span>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={filters.minAmount}
                              onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                              min="0"
                              step="100"
                            />
                          </div>
                        </div>
                        <div className="FeeDefaulters-amountDivider">to</div>
                        <div className="FeeDefaulters-amountInputGroup">
                          <label>
                            <FontAwesomeIcon icon={faSortAmountUp} /> Maximum:
                          </label>
                          <div className="FeeDefaulters-amountInput">
                            <span className="FeeDefaulters-currencySymbol">PKR</span>
                            <input
                              type="number"
                              placeholder="Any amount"
                              value={filters.maxAmount}
                              onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                              min="0"
                              step="100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="FeeDefaulters-filterGroup FeeDefaulters-searchGroup">
                      <label className="FeeDefaulters-filterLabel">
                        <FontAwesomeIcon icon={faSearch} /> Search Students
                      </label>
                      <div className="FeeDefaulters-searchBox">
                        <FontAwesomeIcon icon={faSearch} className="FeeDefaulters-searchIcon" />
                        <input
                          type="text"
                          placeholder="Search by name, father name, student ID, family code, mobile, address..."
                          value={filters.searchQuery}
                          onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                          className="FeeDefaulters-searchInput"
                        />
                        {filters.searchQuery && (
                          <button 
                            className="FeeDefaulters-clearSearch"
                            onClick={() => setFilters(prev => ({ ...prev, searchQuery: "" }))}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      <div className="FeeDefaulters-searchTips">
                        <small>Search tips: Use student ID, name, mobile number, or family code</small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="FeeDefaulters-resultsSummary">
            <div className="FeeDefaulters-resultsInfo">
              <div className="FeeDefaulters-resultsStats">
                <span className="FeeDefaulters-resultsCount">
                  <strong>{filteredStudents.length}</strong> students found
                </span>
                <span className="FeeDefaulters-resultsPending">
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPending)}
                </span>
                {selectedStudents.length > 0 && (
                  <span className="FeeDefaulters-selectedCount">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {selectedStudents.length} selected
                  </span>
                )}
              </div>
              <div className="FeeDefaulters-viewControls">
                <button 
                  className={`FeeDefaulters-viewBtn ${viewMode === 'table' ? 'FeeDefaulters-viewBtnActive' : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  <FontAwesomeIcon icon={faTable} />
                </button>
                <button 
                  className={`FeeDefaulters-viewBtn ${viewMode === 'cards' ? 'FeeDefaulters-viewBtnActive' : ''}`}
                  onClick={() => setViewMode('cards')}
                  title="Card View"
                >
                  <FontAwesomeIcon icon={faIdCard} />
                </button>
                <button 
                  className="FeeDefaulters-viewBtn"
                  onClick={() => {
                    setExpandedStudents(expandedStudents.length === currentStudents.length ? [] : currentStudents.map(s => s.student_db_id))
                  }}
                  title={expandedStudents.length === currentStudents.length ? "Collapse All" : "Expand All"}
                >
                  <FontAwesomeIcon icon={expandedStudents.length === currentStudents.length ? faCompressAlt : faExpandAlt} />
                </button>
              </div>
            </div>
            <div className="FeeDefaulters-pageControls">
              <div className="FeeDefaulters-pageSize">
                <label>Show:</label>
                <select
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="FeeDefaulters-pageSelect"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>per page</span>
              </div>
            </div>
          </div>

          {viewMode === 'table' ? (
            <div className="FeeDefaulters-tableContainer">
              <div className="FeeDefaulters-tableWrapper">
                <table className="FeeDefaulters-table">
                  <thead>
                    <tr>
                      <th className="FeeDefaulters-tableCheckbox">
                        <input
                          type="checkbox"
                          checked={selectedStudents.length === currentStudents.length && currentStudents.length > 0}
                          onChange={selectAllStudents}
                        />
                      </th>
                      <th 
                        className="FeeDefaulters-tableHeader"
                        onClick={() => handleSort("student_name")}
                      >
                        Student Details
                        {sortConfig.key === "student_name" && (
                          <FontAwesomeIcon 
                            icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                            className="FeeDefaulters-sortIcon"
                          />
                        )}
                      </th>
                      <th 
                        className="FeeDefaulters-tableHeader"
                        onClick={() => handleSort("family_code")}
                      >
                        Family Info
                        {sortConfig.key === "family_code" && (
                          <FontAwesomeIcon 
                            icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                            className="FeeDefaulters-sortIcon"
                          />
                        )}
                      </th>
                      <th className="FeeDefaulters-tableHeader">Class & Section</th>
                      <th className="FeeDefaulters-tableHeader">Gender</th>
                      
                      {filters.selectedMonths.map(month => (
                        <th 
                          key={month}
                          className="FeeDefaulters-tableHeader FeeDefaulters-monthColumn"
                          onClick={() => handleSort(`monthly_status.${month}.total_pending`)}
                        >
                          {month}
                          <div className="FeeDefaulters-monthSubtitle">
                            Fee Status
                          </div>
                        </th>
                      ))}
                      
                      <th 
                        className="FeeDefaulters-tableHeader"
                        onClick={() => handleSort("overall_status.total_pending")}
                      >
                        Overall Status
                        {sortConfig.key === "overall_status.total_pending" && (
                          <FontAwesomeIcon 
                            icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                            className="FeeDefaulters-sortIcon"
                          />
                        )}
                      </th>
                      <th className="FeeDefaulters-tableHeader">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudents.map((student, index) => {
                      const isExpanded = expandedStudents.includes(student.student_db_id);
                      const overallStatus = getStatusDisplay(student.overall_status?.status);
                      
                      return (
                        <React.Fragment key={student.student_db_id}>
                          <tr className={selectedStudents.includes(student.student_db_id) ? "FeeDefaulters-rowSelected" : ""}>
                            <td className="FeeDefaulters-tableCheckbox">
                              <input
                                type="checkbox"
                                checked={selectedStudents.includes(student.student_db_id)}
                                onChange={() => handleSelectStudent(student.student_db_id)}
                              />
                            </td>
                            <td className="FeeDefaulters-studentCell">
                              <div className="FeeDefaulters-studentInfo">
                                <strong className="FeeDefaulters-studentName">{student.student_name}</strong>
                                <div className="FeeDefaulters-studentDetails">
                                  <span className="FeeDefaulters-studentId">
                                    <FontAwesomeIcon icon={faIdCard} /> {student.student_id}
                                  </span>
                                  {student.father_name && (
                                    <span className="FeeDefaulters-fatherName">
                                      <FontAwesomeIcon icon={faUser} /> {student.father_name}
                                    </span>
                                  )}
                                  {student.mobile && (
                                    <span className="FeeDefaulters-mobile">
                                      <FontAwesomeIcon icon={faPhone} /> {student.mobile}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="FeeDefaulters-familyInfo">
                                <div className="FeeDefaulters-familyType">
                                  <span className={`FeeDefaulters-familyBadge ${student.family_status || "solo"}`}>
                                    <FontAwesomeIcon icon={student.family_status === "family" ? faUsers : faUser} />
                                    {student.family_status === "family" ? "Family" : "Solo"}
                                  </span>
                                </div>
                                {student.family_code && (
                                  <div className="FeeDefaulters-familyCode">
                                    <FontAwesomeIcon icon={faBuilding} /> {student.family_code}
                                  </div>
                                )}
                                {student.b_form_no && (
                                  <div className="FeeDefaulters-bForm">
                                    <small>B-Form: {student.b_form_no}</small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="FeeDefaulters-classInfo">
                                <div className="FeeDefaulters-classSection">
                                  <FontAwesomeIcon icon={faGraduationCap} />
                                  <span className="FeeDefaulters-className">{student.class || "N/A"}</span>
                                  {student.section && (
                                    <span className="FeeDefaulters-section">Sec: {student.section}</span>
                                  )}
                                </div>
                                {student.address && (
                                  <div className="FeeDefaulters-address">
                                    <small title={student.address}>
                                      <FontAwesomeIcon icon={faHome} /> {student.address.substring(0, 20)}...
                                    </small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="FeeDefaulters-genderCell">
                                <span className={`FeeDefaulters-genderBadge ${student.gender?.toLowerCase()}`}>
                                  <FontAwesomeIcon icon={faVenusMars} />
                                  {student.gender || "N/A"}
                                </span>
                              </div>
                            </td>
                            
                            {filters.selectedMonths.map(month => {
                              const monthStatus = student.monthly_status?.[month];
                              const statusDisplay = getStatusDisplay(monthStatus?.status);
                              
                              return (
                                <td key={month} className="FeeDefaulters-monthCell">
                                  {monthStatus ? (
                                    <div className="FeeDefaulters-monthStatus">
                                      <span 
                                        className="FeeDefaulters-statusBadge"
                                        style={{ backgroundColor: statusDisplay.color }}
                                        title={`${statusDisplay.label} - Pending: ${monthStatus.total_pending}`}
                                      >
                                        <FontAwesomeIcon icon={statusDisplay.icon} />
                                        {statusDisplay.label}
                                      </span>
                                      <div className="FeeDefaulters-monthAmounts">
                                        <div className="FeeDefaulters-monthFee">
                                          <small>Fee: {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(monthStatus.total_monthly_fee || 0)}</small>
                                        </div>
                                        <div className="FeeDefaulters-monthPending">
                                          <small>Pending: {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(monthStatus.total_pending || 0)}</small>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="FeeDefaulters-noDataMonth">
                                      <span className="FeeDefaulters-statusBadge FeeDefaulters-statusNoData">
                                        No Data
                                      </span>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            
                            <td>
                              <div className="FeeDefaulters-overallStatus">
                                <span 
                                  className="FeeDefaulters-statusBadge"
                                  style={{ backgroundColor: overallStatus.color }}
                                >
                                  <FontAwesomeIcon icon={overallStatus.icon} />
                                  {overallStatus.label}
                                </span>
                                <div className="FeeDefaulters-overallAmounts">
                                  <div className="FeeDefaulters-overallFee">
                                    <small>Total Fee: {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_monthly_fee || 0)}</small>
                                  </div>
                                  <div className="FeeDefaulters-overallPending">
                                    <small >Total Pending: {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_pending || 0)}</small>
                                  </div>
                                  {student.overall_status?.paid_months && (
                                    <div className="FeeDefaulters-paidMonths">
                                      <small>Paid: {student.overall_status.paid_months} month(s)</small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="FeeDefaulters-actionButtons">
                                <button 
                                  className="FeeDefaulters-actionBtn FeeDefaulters-detailsBtn"
                                  onClick={() => openStudentDetailsModal(student)}
                                  title="Student Details"
                                >
                                  <FontAwesomeIcon icon={faAddressCard} />
                                </button>
                                <button 
                                  className="FeeDefaulters-actionBtn FeeDefaulters-historyBtn"
                                  onClick={() => openPaymentHistoryModal(student)}
                                  title="Payment History"
                                >
                                  <FontAwesomeIcon icon={faHistory} />
                                </button>
                                <div className="FeeDefaulters-actionDropdown">
                                  <button 
                                    className="FeeDefaulters-actionBtn FeeDefaulters-moreBtn"
                                    title="More Actions"
                                  >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                  </button>
                                  <div className="FeeDefaulters-actionDropdownContent">
                                    <button onClick={() => navigate(`/fee-payment/${student.student_id}`)}>
                                      <FontAwesomeIcon icon={faMoneyBillWave} /> Record Payment
                                    </button>
                                    <button onClick={() => navigate(`/student-details/${student.student_id}`)}>
                                      <FontAwesomeIcon icon={faUser} /> Full Profile
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="FeeDefaulters-expandedRow">
                              <td colSpan={filters.selectedMonths.length + 7}>
                                <div className="FeeDefaulters-expandedContent">
                                  <div className="FeeDefaulters-expandedGrid">
                                    <div className="FeeDefaulters-expandedSection">
                                      <h4>Financial Summary</h4>
                                      <div className="FeeDefaulters-expandedRow">
                                        <span>Current Month Fee:</span>
                                        <strong>{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.current_month_fee || 0)}</strong>
                                      </div>
                                      <div className="FeeDefaulters-expandedRow">
                                        <span>Previous Pending:</span>
                                        <strong className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.previous_pending || 0)}</strong>
                                      </div>
                                      <div className="FeeDefaulters-expandedRow">
                                        <span>Other Expenses:</span>
                                        <strong className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.total_expense_pending || 0)}</strong>
                                      </div>
                                      <div className="FeeDefaulters-expandedRow">
                                        <span>Total Pending:</span>
                                        <strong className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_pending || 0)}</strong>
                                      </div>
                                    </div>
                                    <div className="FeeDefaulters-expandedSection">
                                      <h4>Quick Actions</h4>
                                      <button 
                                        className="FeeDefaulters-btn FeeDefaulters-btnPrimary"
                                        onClick={() => navigate(`/fee-payment/${student.student_id}`)}
                                      >
                                        <FontAwesomeIcon icon={faMoneyBillWave} /> Record Payment
                                      </button>
                                      <button 
                                        className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
                                        onClick={() => openPaymentHistoryModal(student)}
                                      >
                                        <FontAwesomeIcon icon={faHistory} /> View Full History
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {currentStudents.length === 0 && (
                <div className="FeeDefaulters-noData">
                  <FontAwesomeIcon icon={faSearch} size="3x" />
                  <h3>No students found</h3>
                  <p>Try adjusting your filters or select different months</p>
                  <button 
                    className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
                    onClick={clearFilters}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="FeeDefaulters-cardsContainer">
              <div className="FeeDefaulters-cardsGrid">
                {currentStudents.map(student => {
                  const overallStatus = getStatusDisplay(student.overall_status?.status);
                  const paymentPercentage = student.overall_status?.total_monthly_fee > 0 
                    ? ((student.overall_status?.total_paid || 0) / student.overall_status?.total_monthly_fee) * 100 
                    : 0;
                  
                  return (
                    <div 
                      key={student.student_db_id} 
                      className={`FeeDefaulters-card ${selectedStudents.includes(student.student_db_id) ? 'FeeDefaulters-cardSelected' : ''}`}
                    >
                      <div className="FeeDefaulters-cardHeader">
                        <div className="FeeDefaulters-cardCheckbox">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.student_db_id)}
                            onChange={() => handleSelectStudent(student.student_db_id)}
                          />
                        </div>
                        <div className="FeeDefaulters-cardTitle">
                          <h4>{student.student_name}</h4>
                          <div className="FeeDefaulters-cardSubtitle">
                            <span className="FeeDefaulters-studentId">{student.student_id}</span>
                            {student.father_name && (
                              <span className="FeeDefaulters-father">• {student.father_name}</span>
                            )}
                          </div>
                        </div>
                        <span 
                          className="FeeDefaulters-cardStatus"
                          style={{ backgroundColor: overallStatus.color }}
                          title={overallStatus.label}
                        >
                          <FontAwesomeIcon icon={overallStatus.icon} />
                        </span>
                      </div>
                      
                      <div className="FeeDefaulters-cardBody">
                        <div className="FeeDefaulters-cardInfo">
                          <div className="FeeDefaulters-infoRow">
                            <FontAwesomeIcon icon={faUsers} />
                            <div>
                              <small>Family Type & Code</small>
                              <p>
                                <span className={`FeeDefaulters-familyTypeBadge ${student.family_status}`}>
                                  {student.family_status === "family" ? "Family Group" : "Solo Student"}
                                </span>
                                {student.family_code && (
                                  <span className="FeeDefaulters-familyCode"> ({student.family_code})</span>
                                )}
                              </p>
                            </div>
                          </div>
                          
                          <div className="FeeDefaulters-infoRow">
                            <FontAwesomeIcon icon={faGraduationCap} />
                            <div>
                              <small>Class & Section</small>
                              <p>{student.class || "N/A"} {student.section ? `- ${student.section}` : ''}</p>
                            </div>
                          </div>
                          
                          <div className="FeeDefaulters-infoRow">
                            <FontAwesomeIcon icon={faVenusMars} />
                            <div>
                              <small>Gender & Contact</small>
                              <p>
                                {student.gender || "N/A"} • {student.mobile || "No contact"}
                              </p>
                            </div>
                          </div>
                          
                          <div className="FeeDefaulters-monthlyOverview">
                            <small>Monthly Status ({filters.selectedMonths.length} months)</small>
                            <div className="FeeDefaulters-monthlyPills">
                              {filters.selectedMonths.map(month => {
                                const monthStatus = student.monthly_status?.[month];
                                const statusDisplay = getStatusDisplay(monthStatus?.status);
                                
                                return (
                                  <div 
                                    key={month} 
                                    className="FeeDefaulters-monthlyPill"
                                    title={`${month}: ${statusDisplay.label} - Pending: ${new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(monthStatus?.total_pending || 0)}`}
                                  >
                                    <span className="FeeDefaulters-pillMonth">{month.substring(0, 3)}</span>
                                    <span 
                                      className="FeeDefaulters-pillStatus"
                                      style={{ backgroundColor: statusDisplay.color }}
                                    ></span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* New financial breakdown in card view */}
                          <div className="FeeDefaulters-financialBreakdown">
                            <small>Fee Breakdown</small>
                            <div className="FeeDefaulters-financialRow">
                              <span>Current Month Fee:</span>
                              <span>{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.current_month_fee || 0)}</span>
                            </div>
                            <div className="FeeDefaulters-financialRow">
                              <span>Previous Pending:</span>
                              <span className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.previous_pending || 0)}</span>
                            </div>
                            <div className="FeeDefaulters-financialRow">
                              <span>Other Expenses:</span>
                              <span className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.financial_summary?.total_expense_pending || 0)}</span>
                            </div>
                            <div className="FeeDefaulters-financialRow">
                              <span>Total Pending:</span>
                              <span className="FeeDefaulters-unpaid">{new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_pending || 0)}</span>
                            </div>
                          </div>
                          
                          <div className="FeeDefaulters-paymentProgress">
                            <div className="FeeDefaulters-progressHeader">
                              <span>Payment Progress</span>
                              <span>{paymentPercentage.toFixed(1)}%</span>
                            </div>
                            <div className="FeeDefaulters-progressBar">
                              <div 
                                className="FeeDefaulters-progressFill"
                                style={{ 
                                  width: `${Math.min(paymentPercentage, 100)}%`, 
                                  backgroundColor: overallStatus.color 
                                }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="FeeDefaulters-amountDetails">
                            <div className="FeeDefaulters-amountItem">
                              <small>Total Fee</small>
                              <strong>
                                {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_monthly_fee || 0)}
                              </strong>
                            </div>
                            <div className="FeeDefaulters-amountItem">
                              <small>Paid</small>
                              <strong className="FeeDefaulters-amountPaid">
                                {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_paid || 0)}
                              </strong>
                            </div>
                            <div className="FeeDefaulters-amountItem">
                              <small>Pending</small>
                              <strong className="FeeDefaulters-amountPending">
                                {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(student.overall_status?.total_pending || 0)}
                              </strong>
                            </div>
                          </div>
                          
                          {student.b_form_no && (
                            <div className="FeeDefaulters-additionalInfo">
                              <small>Additional Info</small>
                              <p>
                                <FontAwesomeIcon icon={faAddressCard} /> B-Form: {student.b_form_no}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="FeeDefaulters-cardFooter">
                        <button 
                          className="FeeDefaulters-cardBtn FeeDefaulters-cardViewBtn"
                          onClick={() => openStudentDetailsModal(student)}
                        >
                          <FontAwesomeIcon icon={faAddressCard} /> Details
                        </button>
                        <button 
                          className="FeeDefaulters-cardBtn FeeDefaulters-cardActionBtn"
                          onClick={() => openPaymentHistoryModal(student)}
                        >
                          <FontAwesomeIcon icon={faHistory} /> Payment History
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {currentStudents.length === 0 && (
                <div className="FeeDefaulters-noData">
                  <FontAwesomeIcon icon={faSearch} size="3x" />
                  <h3>No students found</h3>
                  <p>Try adjusting your filters or select different months</p>
                </div>
              )}
            </div>
          )}

          {filteredStudents.length > recordsPerPage && (
            <div className="FeeDefaulters-paginationContainer">
              <div className="FeeDefaulters-paginationInfo">
                <span>
                  Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, filteredStudents.length)} of {filteredStudents.length} students
                </span>
                <span className="FeeDefaulters-paginationSummary">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              {renderPagination()}
            </div>
          )}

          <div className="FeeDefaulters-summaryFooter">
            <div className="FeeDefaulters-summaryStats">
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Total Students:</span>
                <span className="FeeDefaulters-summaryValue">{filteredStudents.length}</span>
              </div>
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Total Pending Amount:</span>
                <span className="FeeDefaulters-summaryValue FeeDefaulters-summaryPending">
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPending)}
                </span>
              </div>
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Total Paid Amount:</span>
                <span className="FeeDefaulters-summaryValue FeeDefaulters-summaryPaid">
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPaid)}
                </span>
              </div>
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Previous Pending Total:</span>
                <span className="FeeDefaulters-summaryValue FeeDefaulters-summaryPending">
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPreviousPending)}
                </span>
              </div>
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Other Expenses Total:</span>
                <span className="FeeDefaulters-summaryValue FeeDefaulters-summaryPending">
                  {new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalExpenses)}
                </span>
              </div>
              <div className="FeeDefaulters-summaryItem">
                <span className="FeeDefaulters-summaryLabel">Defaulters:</span>
                <span className="FeeDefaulters-summaryValue FeeDefaulters-summaryDefaulters">
                  {summary.defaultersCount} ({((summary.defaultersCount / filteredStudents.length) * 100).toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="FeeDefaulters-summaryActions">
              <button 
                className="FeeDefaulters-btn FeeDefaulters-btnPrint"
                onClick={() => window.print()}
              >
                <FontAwesomeIcon icon={faPrint} /> Print Report
              </button>
              <button 
                className="FeeDefaulters-btn FeeDefaulters-btnSecondary"
                onClick={() => {
                  Swal.fire({
                    title: 'Report Summary',
                    html: `
                      <div style="text-align: left;">
                        <p><strong>Total Students:</strong> ${filteredStudents.length}</p>
                        <p><strong>Total Pending:</strong> ${new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPending)}</p>
                        <p><strong>Previous Pending:</strong> ${new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalPreviousPending)}</p>
                        <p><strong>Other Expenses:</strong> ${new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(summary.totalExpenses)}</p>
                        <p><strong>Defaulters Count:</strong> ${summary.defaultersCount}</p>
                        <p><strong>Selected Months:</strong> ${filters.selectedMonths.join(', ')}</p>
                        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
                      </div>
                    `,
                    icon: 'info',
                    confirmButtonText: 'OK'
                  })
                }}
              >
                <FontAwesomeIcon icon={faChartPie} /> View Summary
              </button>
            </div>
          </div>
        </section>
      </div>

      {renderStudentDetailsModal()}
      {renderPaymentHistoryModal()}
    </div>
  );
};

export default FeeDefaulters;